import React, { memo, useState, useRef, useMemo } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { useTenantView } from '../TenantViewContext';
import { getNodePulseLevel, isNodeWiredToLunaCore } from '../pulseUtils';
import { Bot, Sparkles, MessageSquare, Palette, Zap, AlertCircle, ArrowRight } from 'lucide-react';

export interface InstructionNodeData {
  personaTitle?: string;
  instructions?: string;
  responseStyle?: 'sales_promo' | 'business_corporate' | 'friendly_concierge' | 'technical_expert' | 'direct_short';
  temperature?: number;
  tone?: string;
  isCustomizable?: boolean;
  [key: string]: unknown;
}

export const InstructionNode = memo(({ id, data, selected }: NodeProps) => {
  const { setNodes, getEdges, getNodes } = useReactFlow();
  const nodeData = (data || {}) as InstructionNodeData;
  const [saveIndicator, setSaveIndicator] = useState<string | null>(null);
  const debounceTimerRef = useRef<any>(null);

  // Local draft states to prevent cursor jumping when typing in the middle of prompts
  const [localInstructions, setLocalInstructions] = useState<string>(() => nodeData.instructions || '');
  const [localTitle, setLocalTitle] = useState<string>(() => nodeData.personaTitle || '');
  const isTypingRef = useRef<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  const syncDebounceTimerRef = useRef<any>(null);

  // Exact selection trackers to ensure typing in the middle never jumps to the end on state/DB re-renders
  const instructionsCursorRef = useRef<{ start: number; end: number } | null>(null);
  const titleCursorRef = useRef<{ start: number; end: number } | null>(null);

  React.useLayoutEffect(() => {
    if (
      instructionsCursorRef.current !== null &&
      textareaRef.current &&
      document.activeElement === textareaRef.current
    ) {
      const { start, end } = instructionsCursorRef.current;
      textareaRef.current.setSelectionRange(start, end);
    }
  });

  React.useLayoutEffect(() => {
    if (
      titleCursorRef.current !== null &&
      titleInputRef.current &&
      document.activeElement === titleInputRef.current
    ) {
      const { start, end } = titleCursorRef.current;
      titleInputRef.current.setSelectionRange(start, end);
    }
  });

  // Sync with incoming nodeData when NOT actively focused / typing
  React.useEffect(() => {
    if (!isTypingRef.current && nodeData.instructions !== undefined && nodeData.instructions !== localInstructions) {
      setLocalInstructions(nodeData.instructions);
    }
  }, [nodeData.instructions]);

  React.useEffect(() => {
    if (!isTypingRef.current && nodeData.personaTitle !== undefined && nodeData.personaTitle !== localTitle) {
      setLocalTitle(nodeData.personaTitle);
    }
  }, [nodeData.personaTitle]);

  const { isTenantView, authToken, tenantApiKey, pulseState, isCompactView, openInspector, openPortalData } = useTenantView();

  const persistNodeDataDirectly = (field: keyof InstructionNodeData, value: any, allUpdates?: Partial<InstructionNodeData>) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const payloadData = {
          ...nodeData,
          [field]: value,
          ...(allUpdates || {})
        };

        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        const token = authToken || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        if (tenantApiKey) {
          headers['x-tenant-api-key'] = tenantApiKey;
        }

        const endpoint = isTenantView ? '/api/tenet/widgets/save-node-data' : '/api/admin/luna-flows/save-node-data';
        const res = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            nodeId: id,
            widgetType: 'INSTRUCTION_NODE',
            data: payloadData,
            widgetData: payloadData,
          }),
        });

        if (res.ok) {
          setSaveIndicator('✅ Saved to DB');
          setTimeout(() => setSaveIndicator(null), 3000);
        }
      } catch (err) {
        console.warn('[InstructionNode] Direct persist warning:', err);
      }
    }, 800);
  };

  const handleChange = (field: keyof InstructionNodeData, value: any) => {
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [field]: value } } : n))
    );
    persistNodeDataDirectly(field, value);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextVal = e.target.value;
    titleCursorRef.current = { start: e.target.selectionStart ?? nextVal.length, end: e.target.selectionEnd ?? nextVal.length };
    setLocalTitle(nextVal);

    if (syncDebounceTimerRef.current) clearTimeout(syncDebounceTimerRef.current);
    syncDebounceTimerRef.current = setTimeout(() => {
      setNodes((nds) =>
        nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, personaTitle: nextVal } } : n))
      );
      persistNodeDataDirectly('personaTitle', nextVal);
    }, 600);
  };

  const handleTitleBlur = () => {
    isTypingRef.current = false;
    if (syncDebounceTimerRef.current) clearTimeout(syncDebounceTimerRef.current);
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, personaTitle: localTitle } } : n))
    );
    persistNodeDataDirectly('personaTitle', localTitle);
  };

  const handleInstructionsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextVal = e.target.value;
    const start = e.target.selectionStart ?? nextVal.length;
    const end = e.target.selectionEnd ?? nextVal.length;
    instructionsCursorRef.current = { start, end };
    setLocalInstructions(nextVal);

    if (syncDebounceTimerRef.current) clearTimeout(syncDebounceTimerRef.current);
    syncDebounceTimerRef.current = setTimeout(() => {
      setNodes((nds) =>
        nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, instructions: nextVal } } : n))
      );
      persistNodeDataDirectly('instructions', nextVal);
    }, 800);
  };

  const handleInstructionsFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    isTypingRef.current = true;
    instructionsCursorRef.current = {
      start: e.currentTarget.selectionStart,
      end: e.currentTarget.selectionEnd,
    };
  };

  const handleInstructionsBlur = () => {
    isTypingRef.current = false;
    if (syncDebounceTimerRef.current) clearTimeout(syncDebounceTimerRef.current);
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, instructions: localInstructions } } : n))
    );
    persistNodeDataDirectly('instructions', localInstructions);
  };

  const handleInsertToken = (token: string) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      const nextVal = localInstructions ? `${localInstructions}\n${token}` : token;
      setLocalInstructions(nextVal);
      handleChange('instructions', nextVal);
      return;
    }
    const start = textarea.selectionStart ?? localInstructions.length;
    const end = textarea.selectionEnd ?? localInstructions.length;
    const current = localInstructions;
    const nextVal = current.substring(0, start) + token + current.substring(end);
    instructionsCursorRef.current = { start: start + token.length, end: start + token.length };
    setLocalInstructions(nextVal);
    setNodes((nds) =>
      nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, instructions: nextVal } } : n))
    );
    persistNodeDataDirectly('instructions', nextVal);
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const pulseLevel = getNodePulseLevel(id, pulseState);
  const isNodeActive = pulseLevel === 'active';
  const isNodeTrail = pulseLevel === 'trail';
  const isWired = useMemo(() => isNodeWiredToLunaCore(id, getEdges(), getNodes()), [id, getEdges, getNodes]);

  return (
    <div
      className={`group rounded-2xl bg-gradient-to-b from-[#0C101E]/98 via-[#070A14]/98 to-[#04060C]/98 backdrop-blur-2xl border transition-all duration-300 w-[380px] shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)] relative select-none ${
        isNodeActive
          ? 'border-cyan-300 ring-4 ring-cyan-400/60 shadow-[0_0_80px_rgba(34,211,238,0.8),inset_0_1px_0_0_rgba(255,255,255,0.2)] scale-[1.02] pulse-glow-active'
          : isNodeTrail
          ? 'border-emerald-400/70 ring-2 ring-emerald-400/30 shadow-[0_0_40px_rgba(52,211,153,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : selected
          ? 'border-sky-400 ring-2 ring-sky-400/40 shadow-[0_0_30px_rgba(56,189,248,0.4),inset_0_1px_0_0_rgba(255,255,255,0.15)]'
          : 'border-sky-500/35 hover:border-sky-400/80 shadow-[0_20px_50px_rgba(0,0,0,0.85),0_0_20px_rgba(56,189,248,0.15),inset_0_1px_0_0_rgba(255,255,255,0.12)]'
      }`}
    >
      {/* ── Floating Cyber-HUD Action: View Live Data in Portal (Fades in on Hover) ── */}
      {openPortalData && (
        <div className="absolute -top-11 left-1/2 -translate-x-1/2 z-50 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-300 transform translate-y-1 group-hover:translate-y-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openPortalData('lunaCoreNode', id);
            }}
            className="nodrag flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-cyan-950 via-slate-900 to-blue-950 border-2 border-cyan-400 text-cyan-200 font-extrabold text-[11px] uppercase tracking-wider shadow-[0_0_25px_rgba(6,182,212,0.8)] hover:border-cyan-300 hover:scale-105 hover:text-white transition-all cursor-pointer whitespace-nowrap backdrop-blur-md active:scale-95"
            title="Open Portal Data to view persona instructions & core settings"
          >
            <Bot size={13} className="text-cyan-300 animate-pulse" />
            <span>View Data in Portal</span>
            <ArrowRight size={13} className="text-cyan-400" />
          </button>
        </div>
      )}
      {/* ── Directional Connection Handles (Left in / Right out to Luna Core) ── */}
      <Handle
        type="target"
        position={Position.Left}
        id="in-instruction"
        className="!bg-cyan-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-left-2 transition-transform hover:scale-125 z-20 shadow-[0_0_8px_rgba(6,182,212,0.8)]"
        title="Input: Chained Prompt"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="out-instruction"
        className="!bg-cyan-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-right-2 transition-transform hover:scale-125 z-20 shadow-[0_0_8px_rgba(6,182,212,0.8)]"
        title="Output to Luna Core Hub"
      />

      {/* Target Connection Handle (Top) */}
      <Handle
        type="target"
        position={Position.Top}
        id="in-top"
        className="!bg-cyan-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-top-2 transition-transform hover:scale-125"
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-sky-950/80 via-[#0B101E]/95 to-slate-900/80 px-4 py-3 border-b border-white/[0.08] flex items-center justify-between rounded-t-2xl shadow-[inset_0_1px_0_0_rgba(255,255,255,0.1)]">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 shadow-[0_0_12px_rgba(6,182,212,0.3)]">
            <Bot size={16} />
          </div>
          <div>
            <h4 className="text-xs font-black text-slate-100 tracking-wide">System Persona</h4>
            <p className="text-[10px] text-cyan-300/70 font-mono">Agent Identity & Rules</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {saveIndicator && (
            <span className="text-[9px] font-mono font-bold text-emerald-400 animate-pulse bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/30">
              {saveIndicator}
            </span>
          )}
          <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
            PROMPT
          </span>
        </div>
      </div>

      {/* Wiring Live Status Bar */}
      <div className="mx-4 mt-3">
        {isWired ? (
          <div className="px-2.5 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <Zap size={11} className="text-emerald-400" /> Wired to Luna Core Hub
            </span>
            <span className="text-[9px] font-bold text-emerald-400 uppercase tracking-wide">ACTIVE</span>
          </div>
        ) : (
          <div className="px-2.5 py-1 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[10px] flex items-center justify-between font-mono">
            <span className="flex items-center gap-1.5">
              <AlertCircle size={11} className="text-amber-400" /> Disconnected
            </span>
            <span className="text-[9px] text-amber-400/80">Connect to Luna Core Hub</span>
          </div>
        )}
      </div>

      {/* Body */}
      {isCompactView ? (
        <div className="p-4 space-y-2.5 text-xs">
          <div className="p-3 rounded-2xl bg-slate-900/80 border border-cyan-500/20 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-bold text-white text-sm">
                  {nodeData.personaTitle || 'AI Assistant'}
                </h4>
                <p className="text-[10px] text-cyan-300 font-mono">Persona & System Instructions</p>
              </div>
              <button
                type="button"
                onClick={() => openInspector && openInspector(id)}
                className="nodrag px-2.5 py-1 rounded-lg text-[10px] font-bold bg-cyan-500/20 hover:bg-cyan-500/35 text-cyan-200 border border-cyan-400/40 transition cursor-pointer active:scale-95 shadow-sm"
              >
                Inspect ⚙️
              </button>
            </div>
            <p className="text-[10px] text-slate-400 line-clamp-2 leading-relaxed font-mono">
              {nodeData.instructions || 'No directives configured.'}
            </p>
          </div>
        </div>
      ) : (
        <div className="p-4 space-y-3.5 text-xs">
          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1.5">
              <Sparkles size={12} className="text-cyan-400" /> Persona / Role Title
            </label>
            <input
              ref={titleInputRef}
              type="text"
              className="nodrag nowheel w-full bg-slate-900/90 border border-white/10 rounded-xl px-3 py-2 text-slate-100 text-xs focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/30 transition-all placeholder:text-slate-600 font-bold"
              placeholder="e.g. Hotel Receptionist AI"
              value={localTitle}
              onChange={handleTitleChange}
              onFocus={(e) => {
                isTypingRef.current = true;
                titleCursorRef.current = {
                  start: e.currentTarget.selectionStart ?? localTitle.length,
                  end: e.currentTarget.selectionEnd ?? localTitle.length,
                };
              }}
              onSelect={(e) => {
                titleCursorRef.current = {
                  start: e.currentTarget.selectionStart ?? localTitle.length,
                  end: e.currentTarget.selectionEnd ?? localTitle.length,
                };
              }}
              onKeyUp={(e) => {
                titleCursorRef.current = {
                  start: e.currentTarget.selectionStart ?? localTitle.length,
                  end: e.currentTarget.selectionEnd ?? localTitle.length,
                };
              }}
              onClick={(e) => {
                titleCursorRef.current = {
                  start: e.currentTarget.selectionStart ?? localTitle.length,
                  end: e.currentTarget.selectionEnd ?? localTitle.length,
                };
              }}
              onBlur={handleTitleBlur}
            />
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1.5">
              <MessageSquare size={12} className="text-cyan-400" /> Prompt & Behavioral Instructions
            </label>
            <textarea
              ref={textareaRef}
              rows={4}
              className="nodrag nowheel w-full bg-slate-900/90 border border-white/10 rounded-xl px-3 py-2 text-slate-100 text-xs focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/30 transition-all placeholder:text-slate-600 resize-none font-sans leading-relaxed"
              placeholder="e.g. You are Luna, assisting guests at the front desk..."
              value={localInstructions}
              onChange={handleInstructionsChange}
              onFocus={handleInstructionsFocus}
              onSelect={(e) => {
                instructionsCursorRef.current = {
                  start: e.currentTarget.selectionStart ?? localInstructions.length,
                  end: e.currentTarget.selectionEnd ?? localInstructions.length,
                };
              }}
              onKeyUp={(e) => {
                instructionsCursorRef.current = {
                  start: e.currentTarget.selectionStart ?? localInstructions.length,
                  end: e.currentTarget.selectionEnd ?? localInstructions.length,
                };
              }}
              onClick={(e) => {
                instructionsCursorRef.current = {
                  start: e.currentTarget.selectionStart ?? localInstructions.length,
                  end: e.currentTarget.selectionEnd ?? localInstructions.length,
                };
              }}
              onBlur={handleInstructionsBlur}
            />
            <div className="flex items-center justify-between mt-1.5">
              <span className="text-[9px] font-mono text-slate-500">Tokens:</span>
              <button
                type="button"
                onClick={() => handleInsertToken('@order_template')}
                className="px-2 py-0.5 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-300 text-[9px] font-mono font-bold flex items-center gap-1 transition cursor-pointer"
                title="Insert @order_template so Luna automatically follows store order intake format"
              >
                <span>📦 @order_template</span>
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between pt-1 border-t border-white/5">
            <span className="text-[10px] text-slate-400 font-medium">Creativity (Temp):</span>
            <span className="text-[10px] font-mono text-cyan-300 bg-cyan-950/60 px-2 py-0.5 rounded border border-cyan-500/20">
              {nodeData.temperature ?? 0.7}
            </span>
          </div>
        </div>
      )}

      {/* Source Connection Handle (Bottom) */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="out-bottom"
        className="!bg-cyan-400 !w-3.5 !h-3.5 !border-2 !border-slate-950 !-bottom-2 transition-transform hover:scale-125"
      />
    </div>
  );
});

InstructionNode.displayName = 'InstructionNode';
export default InstructionNode;
